import { useEffect, useMemo, useState } from 'react'
import { Loader2, Plus, X } from 'lucide-react'
import { WorkTypesService } from '../../services/workTypesService'
import {
  UNCATEGORISED_COLOR,
  type WorkTypeCategory,
} from '../../services/workTypeCategoriesService'
import {
  WORK_TYPE_LEVELS,
  WORK_TYPE_LEVEL_META,
  inferWorkTypeLevelFromName,
  type WorkTypeLevel,
} from '../../utils/workTypeLevels'
import { WorkTypeLevelBadge } from './WorkTypeLevelBadge'
import { TeamMultiSelect } from './TeamMultiSelect'

interface AddWorkTypeModalProps {
  isOpen: boolean
  /** Team the Add button was clicked from — pre-selected. */
  initialTeamName: string
  teamOptions: string[]
  categories: WorkTypeCategory[]
  onClose: () => void
  onCreated: () => Promise<void> | void
}

const UNCATEGORISED_VALUE = ''

export function AddWorkTypeModal({
  isOpen,
  initialTeamName,
  teamOptions,
  categories,
  onClose,
  onCreated,
}: AddWorkTypeModalProps) {
  const service = useMemo(() => WorkTypesService.getInstance(), [])
  const [name, setName] = useState('')
  const [teams, setTeams] = useState<string[]>([])
  const [categoryId, setCategoryId] = useState(UNCATEGORISED_VALUE)
  const [level, setLevel] = useState<WorkTypeLevel>(2)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const options = useMemo(() => {
    const set = new Set(teamOptions)
    if (initialTeamName) set.add(initialTeamName)
    return [...set]
  }, [teamOptions, initialTeamName])

  useEffect(() => {
    if (!isOpen) return
    setName('')
    setTeams(initialTeamName ? [initialTeamName] : [])
    setCategoryId(UNCATEGORISED_VALUE)
    setLevel(2)
    setError('')
  }, [isOpen, initialTeamName])

  if (!isOpen) return null

  const selectedCategory =
    categoryId === UNCATEGORISED_VALUE
      ? null
      : categories.find((c) => c.id === categoryId) || null
  const previewColor = selectedCategory?.color || UNCATEGORISED_COLOR
  const previewCategoryLabel = selectedCategory?.name || 'Uncategorised'

  const handleNameChange = (next: string) => {
    setName(next)
    setLevel(inferWorkTypeLevelFromName(next))
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Work type name is required')
      return
    }
    if (teams.length === 0) {
      setError('Select at least one team')
      return
    }
    try {
      setSaving(true)
      setError('')
      await service.createWorkType({
        name: name.trim(),
        teams,
        categoryId: categoryId === UNCATEGORISED_VALUE ? null : categoryId,
        level,
      })
      await onCreated()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create work type')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Add work type</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teams *
            </label>
            <TeamMultiSelect options={options} value={teams} onChange={setTeams} />
          </div>

          <div>
            <label
              htmlFor="work-type-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name *
            </label>
            <input
              id="work-type-name"
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g., Seismic restraint design for partitions"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              autoFocus
            />
          </div>

          <div>
            <label
              htmlFor="work-type-category"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Category
            </label>
            <select
              id="work-type-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value={UNCATEGORISED_VALUE}>Uncategorised</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="work-type-level"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Level
            </label>
            <select
              id="work-type-level"
              value={level}
              onChange={(e) => setLevel(Number(e.target.value) as WorkTypeLevel)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {WORK_TYPE_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {WORK_TYPE_LEVEL_META[l].shortLabel}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div
              className="relative h-[10.5rem] rounded-xl border border-gray-200 p-4 flex flex-col"
              style={{
                borderTopColor: previewColor,
                borderTopWidth: 3,
                backgroundColor: `${previewColor}08`,
              }}
            >
              <p className="text-sm font-medium text-gray-900 leading-snug line-clamp-5">
                {name.trim() || 'Work type name'}
              </p>
              <div className="mt-auto pt-2 flex items-end justify-between gap-2">
                <p className="text-xs font-medium truncate" style={{ color: previewColor }}>
                  {previewCategoryLabel}
                </p>
                <WorkTypeLevelBadge level={level} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-sm"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  )
}
